import React, { useCallback, useState } from 'react';

import {
  Collection,
  LegalAgreements,
  LegalDocument,
  LegalDocumentType,
  translateLegalDocumentType,
} from '../../../../../../api-client';
import { Agreement } from '../../../../../../api-client/models/documents/Agreement';
import documentIcon from '../../../../../../assets/images/svg/document.svg';
import { Image, Pagination } from '../../../../../../components';
import { createAgreementProvider } from '../../../../../../di';
import { formatDate } from '../../../../../../formatters';
import { useErrorCallback } from '../../../../../../hooks';
import { AgentDocumentAgreement } from '../DocumentsElements/AgentDocumentAgreement';
import { PageOutOfRangeGuard } from '../DocumentsElements/PageOutOfRangeGuard';
import stylesItems from './../DocumentsElements/DocumentsToProjectsItem.module.scss';
import styles from './AgentDocuments.module.scss';

const AgentDocuments = () => {
  const [loading, setLoading] = useState(true);
  const [agentDocuments, setAgentDocuments] = useState<LegalAgreements>();
  const handleError = useErrorCallback();

  const loadData = useCallback(async (page: number, perPage: number) => {
    setLoading(true);
    try {
      const provider = await createAgreementProvider();
      const results = await provider.getLegalAgreements(page, perPage);
      setAgentDocuments(results);
    } catch (error: any) {
      await handleError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const renderedDocuments = (documents: Collection<Agreement>) => {
    return (
      <>
        {documents.items.map((document: Agreement) => {
          return <AgentDocumentAgreement document={document} key={document.uuid} />;
        })}
      </>
    );
  };

  const renderedLegalDocuments = (documents: Collection<LegalDocument>) => {
    return (
      <>
        {documents.items.map((document: LegalDocument) => {
          const isAgentAgreement =
            document.type === LegalDocumentType.AGENT ||
            document.type === LegalDocumentType.AGENT_CHANGE;
          return (
            <ul key={document.uuid} className={stylesItems.documentsToProjectBody__list}>
              <li className={stylesItems.documentsToProjectBody__item}>
                <Image className={stylesItems.documentsToProjectBody__icon} src={documentIcon} />
                <a
                  href={document.url}
                  target="_blank"
                  rel="noreferrer"
                  className={stylesItems.documentsToProjectBody__title}
                >
                  {document.type === LegalDocumentType.COMMON
                    ? document.title
                    : translateLegalDocumentType(document.type)}
                </a>
                <a
                  href={document.url}
                  target="_blank"
                  rel="noreferrer"
                  className={stylesItems.documentsToProjectBody__size}
                >
                  pdf, {document.size} Кб
                </a>
                <a
                  href={document.url}
                  target="_blank"
                  rel="noreferrer"
                  className={stylesItems.documentsToProjectBody__watch}
                >
                  Нажмите, чтобы посмотреть
                </a>
                <a
                  href={document.url}
                  target="_blank"
                  rel="noreferrer"
                  className={stylesItems.documentsToProjectBody__date}
                >
                  {formatDate(new Date(document.effectiveDate || ''))}
                </a>
                <a
                  href={document.url}
                  target="_blank"
                  rel="noreferrer"
                  className={stylesItems.documentsToProjectBody__dateText}
                >
                  {isAgentAgreement ? 'дата вступления в силу' : 'дата подписания'}
                </a>
              </li>
            </ul>
          );
        })}
      </>
    );
  };

  const legalDocumentsCount = agentDocuments?.collectionLegalDocuments.count ?? 0;
  const legalDocumentsTotalCount = agentDocuments?.collectionLegalDocuments.totalCount ?? 0;
  const agreementsCount = agentDocuments?.collectionAgreements.count ?? 0;

  const count = agreementsCount + legalDocumentsCount;
  const totalCount = legalDocumentsTotalCount;
  return (
    <div className={styles.documents}>
      <Pagination onLoad={loadData} count={count} totalCount={totalCount}>
        <PageOutOfRangeGuard loading={loading} />
        <div className={styles.documents__container}>
          <Pagination.Content loading={loading} variant="documents">
            {agentDocuments && renderedLegalDocuments(agentDocuments.collectionLegalDocuments)}
            {agentDocuments && renderedDocuments(agentDocuments.collectionAgreements)}
          </Pagination.Content>
        </div>
        <Pagination.Paginator />
      </Pagination>
    </div>
  );
};

export default AgentDocuments;
